import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faEnvelope } from "@fortawesome/free-regular-svg-icons";
import { faFacebook, faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { faShare } from "@fortawesome/pro-light-svg-icons";
import ClientOnly from "ClientOnly";

import type { ShareSupplierButtonProps, ShareSupplierPackageModalProps } from './types';

import scss from './SharePackageButton.module.scss'

const ShareSupplierPackageModal = ({shareUrl, facebookAppId, onHideModal} : ShareSupplierPackageModalProps) => {
    const [showCopySuccess, setShowCopySuccess] = React.useState(false);
    const [fbLink, setFbLink] = React.useState('');
    const [whatsappLink, setWhatsappLink] = React.useState('');
    const [emailLink, setEmailLink] = React.useState('');

    useEffect(() => {
        setFbLink(`https://www.facebook.com/dialog/share?_encoding=UTF8&app_id=${facebookAppId}&display=popup&href=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(shareUrl)}&hashtag=%23EasyWeddings`);

        setWhatsappLink(`https://wa.me/?text=${encodeURIComponent(`Check out this Easy Weddings supplier package: ${shareUrl}`)}`);
        
        setEmailLink(`mailto:?subject=${encodeURI('YOUR opinion is requested on this wedding package')}&body=${encodeURIComponent(`Help someone special build their dream day!\nThis Easy Weddings Supplier Package has been created with help from our experts.\nIt's the perfect solution for a seamless wedding planning experience. Take a look here.\n${shareUrl}`)}`);
    }, []);

    const onCopyLink = () => {
        // This functionality is only available when using HTTPS
        if(!!navigator.clipboard) {
            navigator.clipboard.writeText(shareUrl)
                .then(() => {
                    setShowCopySuccess(true);

                    setTimeout(() => {
                        setShowCopySuccess(false);
                    }, 3000);
                })
                .catch((error) => {
                    console.error('Failed to copy: ', error);
                });
        } else {
            console.error('Clipboard API not available - check that the page is using HTTPS');
        }
    };

    return (
        <Modal show={true} onHide={onHideModal} size="lg" className={scss.shareModal}>
            <Modal.Header closeButton className="shareHeader"><h4 className="mb-0 mt-2">Share your package URL</h4></Modal.Header>
            <Modal.Body>
                <div className="row mb-3">
                    <div className="col-12 text-center">
                        <a className="share-links m-3" target="popup" href={fbLink}>
                            <Icon icon={faFacebook} className="text-primary fa-xl" />
                        </a>
                        <a className="share-links m-3" target="_blank" href={whatsappLink}>
                            <Icon icon={faWhatsapp} className="text-primary fa-xl" />
                        </a>
                        <a className="share-links m-3" href={emailLink}>
                            <Icon icon={faEnvelope} className="text-primary fa-xl" />
                        </a>
                    </div>
                </div>
                <div className="row justify-content-center">
                    <div className={`col-12 col-lg-9 ${scss.copyUrlWrapper}`}>
                        <input type="text" className={`w-100 form-control ${scss.copyLink}`} value={shareUrl} readOnly />
                        <Button onClick={onCopyLink} variant={'link'} className={scss.copyButton}>Copy</Button>
                    </div>
                </div>
                <div className="row justify-content-center">
                    <div className={`col-7 col-lg-4 ${scss.copySuccess}`}> 
                        <div className={`${scss.copyMessage} ${!showCopySuccess ? scss.hidden : ''}`}>
                            <Icon icon={faCheckCircle} className={scss.copySuccessIcon} /> 
                            Copied to clipboard
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    )
}

const SharePackageButton = ({ packageName, facebookAppId } : ShareSupplierButtonProps) => {
    const [showLinkShareModal, setShowLinkShareModal] = useState<boolean>(false);
    const [shareLinkUrl, setShareLinkUrl] = useState<string>('');

    const ShareModal = React.lazy(() => Promise.resolve({ default: ShareSupplierPackageModal }));

    useEffect(() => {
        setShareLinkUrl(document.location.href);
    }, [packageName]);

    const hideShareModal = useCallback(() => {
        setShowLinkShareModal(false)
    }, [setShowLinkShareModal]);

    const showShareModal = useCallback(() => {
        setShowLinkShareModal(true)
    }, [setShowLinkShareModal]);

    const handleShareClick = () => {
        if (!!navigator.share && isMobile()) {
            const shareOptions = { 
                title: packageName, 
                url: shareLinkUrl 
            };
        
            try {
                navigator.share(shareOptions)
                    .then(val => console.log(`Native share succeeded ${val}`))
                    .catch(error => console.log("Native share failed:", error));
            } catch (err) {
                console.log("Share failed:", err);
            }
        }
        else {
            showShareModal();
        }
    };

    const isMobile = () => /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    return (
        <>
            <Button variant={'primary'} className={scss.shareBtn} onClick={handleShareClick}>
                <Icon icon={faShare} className={scss.shareIcon} style={{maxWidth: '2rem', maxHeight: '2rem'}}/>
                Share
            </Button>
            {showLinkShareModal && <ClientOnly<ShareSupplierPackageModalProps> Component={ShareModal} shareUrl={shareLinkUrl} facebookAppId={facebookAppId} onHideModal={hideShareModal} />}
        </>
    );
}

export default SharePackageButton